import { useEffect, useRef, useState } from "react";
import { BadgeCheck, Clock, XCircle, Upload, Loader2, ShieldCheck, Landmark } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function DocSlot({ docKey, label, url, onUpload }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpload(r.data.url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <div data-testid={`hostdoc-${docKey}`}>
      <button onClick={() => inputRef.current?.click()} disabled={busy}
        className={`w-full aspect-[4/3] rounded-xl border-2 grid place-items-center overflow-hidden transition-colors ${url ? "border-green-500" : "border-dashed border-slate-300 hover:border-blue-400 bg-slate-50"}`}>
        {url ? <img src={url} alt={label} className="w-full h-full object-cover" />
          : busy ? <Loader2 size={18} className="animate-spin text-blue-600" />
          : <Upload size={18} className="text-slate-400" />}
      </button>
      <p className={`text-[11px] font-semibold mt-1.5 text-center ${url ? "text-green-700" : "text-slate-500"}`}>{label}</p>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid={`hostdoc-input-${docKey}`}
        onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
}

export function HostVerificationCard() {
  const [v, setV] = useState(null);
  const [docs, setDocs] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/host/me/verification").then((r) => { setV(r.data); setDocs(r.data.documents || {}); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/host/me/verification", { documents: docs });
      toast.success("Documents submitted! We'll review them within 1–2 business days.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!v) return null;
  const required = v.required_docs || [];
  const allUploaded = required.every((d) => docs[d.key]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-8" data-testid="host-verification-card">
      <h3 className="font-heading text-lg font-semibold mb-1 flex items-center gap-2"><BadgeCheck size={18} className="text-blue-600" /> Verified Host status</h3>

      {v.status === "approved" ? (
        <div className="mt-4 flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-4" data-testid="host-verified-status">
          <BadgeCheck size={20} className="text-green-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-green-800">You're a Verified Host 🎉</p>
            <p className="text-xs text-green-700 mt-0.5">The Verified Host badge appears on your profile and all your listings.</p>
          </div>
        </div>
      ) : v.status === "pending" ? (
        <div className="mt-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-4" data-testid="host-pending-status">
          <Clock size={20} className="text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">Your documents are under review — we'll email you within 1–2 business days.</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-slate-500 mb-1">Earn a <b>Verified Host</b> badge on your profile and listings by submitting these documents once:</p>
          {v.status === "rejected" && (
            <div className="my-3 flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="host-rejected-status">
              <XCircle size={16} className="text-red-500 shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">Your last submission wasn't approved{v.notes ? ` — "${v.notes}"` : ""}. Please re-upload corrected documents below.</p>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
            {required.map((d) => (
              <DocSlot key={d.key} docKey={d.key} label={d.label} url={docs[d.key]}
                onUpload={(url) => setDocs((p) => ({ ...p, [d.key]: url }))} />
            ))}
          </div>
          <p className={`mt-4 text-xs font-semibold flex items-center gap-1.5 ${v.bank_on_file ? "text-green-600" : "text-amber-600"}`}>
            <Landmark size={13} /> {v.bank_on_file ? "Payout bank account on file ✓" : "Tip: add your payout bank details above — required for payouts."}
          </p>
          <button onClick={submit} disabled={saving || !allUploaded} data-testid="host-verification-submit"
            className="mt-5 rounded-full bg-blue-600 text-white px-6 py-2.5 font-semibold text-sm hover:bg-blue-700 disabled:opacity-40 flex items-center gap-2">
            {saving ? <><Loader2 size={15} className="animate-spin" /> Submitting…</> : <><ShieldCheck size={15} /> Submit for verification</>}
          </button>
        </>
      )}
    </div>
  );
}
